import { message } from 'antd';
import {
	getFileLinkFromString,
	getProtectedImageUriFromString,
	getProtectedImageUriFromStringResized,
} from './FileAccessUtils';
import * as Sentry from '@sentry/react';

export const getResizedImageUrl = (filePath, width, height) =>
	`https://images.emptor.ai/fit-in/${width}x${height}/${filePath}`;

export const normFile = (e) => {
	if (Array.isArray(e)) {
		return e;
	}
	return e && e.fileList;
};

export const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];

// iPhone photos. Kept separate from `imageMimeTypes` because no browser can *render* these
// bytes — they upload, and the backend's converter produces the JPEG derivative that actually
// gets displayed (see ConvertedImageUtils).
export const heicMimeTypes = [
	'image/heic',
	'image/heif',
	'image/heic-sequence',
	'image/heif-sequence',
];
export const heicFileExtensions = ['.heic', '.heif'];

// Browsers disagree on what an iPhone photo is: Safari reports `image/heic`, while Chrome and
// Firefox frequently report an empty string because they have no mapping for the container. So
// the MIME check alone rejects a valid HEIC on those browsers — fall back to the file name.
export const isHeicFile = (file) =>
	heicMimeTypes.indexOf(file.type) !== -1 ||
	heicFileExtensions.some((extension) =>
		String(file.name || '')
			.toLowerCase()
			.endsWith(extension)
	);

// HEIC is wired end to end only for work order photos, attachments and chat-note photos: those
// are the only entities the backend hydrates a JPEG derivative onto today. Every other upload
// surface keeps plain `image/*` / `imageMimeTypes` and `imageUploadValidation`, all of which
// reject HEIC, so an iPhone photo can never be stored where nothing can render it. Widen one
// surface at a time as the backend ships hydration for its entity.
//
// For `accept` on the work order controls that enumerate MIME types (chat and note composers):
// the renderable types plus HEIC by both MIME and extension, since the extension is the only
// thing that makes an iPhone photo selectable in a browser that doesn't know the MIME type.
export const workOrderImageUploadAccept = [
	...imageMimeTypes,
	...heicMimeTypes,
	...heicFileExtensions,
].join(',');

// For `accept` on the work order controls that advertise the broad `image/*` wildcard. The
// wildcard alone hides .heic in browsers that have no MIME mapping for the container, so the
// extensions are spelled out alongside it.
export const workOrderImageWildcardUploadAccept = ['image/*', ...heicFileExtensions].join(',');

const validateImageUpload = (file, allowHeic: boolean) => {
	const isImage = imageMimeTypes.indexOf(file.type) !== -1 || (allowHeic && isHeicFile(file));
	if (!isImage) {
		message.error(
			allowHeic
				? 'You can only upload jpeg, png, gif, svg or heic image files!'
				: 'You can only upload jpeg, png, gif or svg image files!'
		);
	}
	const isLt20M = file.size / 1024 / 1024 < 20;
	if (!isLt20M) {
		message.error('Image file size must smaller than 20MB!');
	}
	return isImage && isLt20M;
};

export function imageUploadValidation(file) {
	return validateImageUpload(file, false);
}

// Pairs with the `workOrderImage*UploadAccept` lists above: same 20MB cap, HEIC allowed.
export function workOrderImageUploadValidation(file) {
	return validateImageUpload(file, true);
}

// True for any video/* MIME type. Used where a gallery accepts both images and
// videos (e.g. the deliverable gallery), so uploads and GalleryItem.fileType can
// distinguish the two.
export const isVideoMimeType = (mimeType) =>
	typeof mimeType === 'string' && mimeType.indexOf('video/') === 0;

// Validation for a gallery that advertises `image/*,video/*`: allow image OR
// video files (still capped at 20MB). Pairs with `accept="image/*,video/*"` so
// videos the control advertises aren't rejected by the image-only check.
export function mediaUploadValidation(file) {
	const isImage = imageMimeTypes.indexOf(file.type) !== -1;
	const isVideo = isVideoMimeType(file.type);
	if (!isImage && !isVideo) {
		message.error('You can only upload image or video files!');
	}
	const isLt20M = file.size / 1024 / 1024 < 20;
	if (!isLt20M) {
		message.error('File size must be smaller than 20MB!');
	}
	return (isImage || isVideo) && isLt20M;
}

const hRange = [0, 360];
const sRange = [0, 100];
const lRange = [0, 100];

const getHashOfString = (str: string) => {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	hash = Math.abs(hash);
	return hash;
};

const normalizeHash = (hash: number, min: number, max: number) => {
	return Math.floor((hash % (max - min)) + min);
};

const generateHSL = (name: string) => {
	const hash = getHashOfString(name);
	const h = normalizeHash(hash, hRange[0], hRange[1]);
	const s = normalizeHash(hash, sRange[0], sRange[1]);
	const l = normalizeHash(hash, lRange[0], lRange[1]);
	return [h, s, l];
};

const HSLtoString = (hsl) => {
	return `hsl(${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%)`;
};

export const getAvatarStyle = (name) => {
	const hsl = generateHSL(name || '');
	return {
		backgroundColor: HSLtoString(hsl),
		color: hsl[2] > 50 ? '#000' : '#FFF',
	};
};

const fetchAndDownload = async (imageUrl, imageName, isFinalAttempt = false) => {
	// Fetch the image
	const response = await fetch(imageUrl, { method: 'GET' });
	if (!response.ok) {
		if (isFinalAttempt) {
			Sentry.captureException(`Error downloading image: ${imageName}`);
			message.error('Failed to download an image.');
		} else {
			throw new Error(`Failed to fetch image ${imageName}`);
		}
		return;
	}

	const blob = await response.blob();
	const mimeType = blob.type || 'image/png'; // Default to PNG
	const extension = mimeType.split('/')[1]; // Extract file extension

	const url = window.URL.createObjectURL(blob);

	// Create and trigger the download link
	const link = document.createElement('a');
	link.href = url;
	link.download = `${imageName}.${extension}`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	// Cleanup memory
	window.URL.revokeObjectURL(url);
};

export const fallbackDownloadImages = async (photoStrings, role, name = '') => {
	for (let i = 0; i < photoStrings.length; i++) {
		const imageUrl = photoStrings[i];
		const imageName = `${name}image_${i + 1}`;
		try {
			const cdnUrl = getProtectedImageUriFromString(role)(imageUrl);
			await fetchAndDownload(cdnUrl, imageName);
		} catch (error) {
			const backEndUrl = getFileLinkFromString(role)(imageUrl);
			await fetchAndDownload(backEndUrl, imageName, true);
		}
	}
};

const fileSystemAccessAPIFetchAndDownload = async (directoryHandle, imageUrl, imageName) => {
	const response = await fetch(imageUrl);
	if (!response.ok) throw new Error(`Failed to fetch ${imageUrl}`);

	const blob = await response.blob();
	const fileHandle = await directoryHandle.getFileHandle(imageName, { create: true });
	const writableStream = await fileHandle.createWritable();
	await writableStream.write(blob);
	await writableStream.close();
};

// File System Access API (Chrome/Edge)
const downloadImagesUsingFSAPI = async (photoStrings, role, name = '') => {
	try {
		const directoryHandle = await window.showDirectoryPicker();

		for (let i = 0; i < photoStrings.length; i++) {
			const imageUrl = photoStrings[i];
			const imageName = `${name || 'Downloaded_Image'}_${i + 1}.jpg`;
			try {
				const cdnUrl = getProtectedImageUriFromString(role)(imageUrl);
				await fileSystemAccessAPIFetchAndDownload(directoryHandle, cdnUrl, imageName);
			} catch (error) {
				const backEndUrl = getFileLinkFromString(role)(imageUrl);
				await fileSystemAccessAPIFetchAndDownload(directoryHandle, backEndUrl, imageName);
			}
		}

		message.success('All images saved successfully!');
	} catch (error) {
		console.error('Error accessing file system:', error);
		message.error('Could not save images.');
	}
};

export const downloadImages = async (photoStrings, role, name = '') => {
	if (!photoStrings.length) return;

	const isAllowedShowDirectoryPicker = 'showDirectoryPicker' in window;

	if (isAllowedShowDirectoryPicker) {
		return await downloadImagesUsingFSAPI(photoStrings, role, name);
	} else {
		return await fallbackDownloadImages(photoStrings, role, name);
	}
};
export const inlineImagesAsBase64 = async (container: HTMLElement) => {
	const images = container.querySelectorAll('img');

	await Promise.all(
		Array.from(images).map(async (img) => {
			let src = img.src;
			if (!src || src.startsWith('data:') || src.startsWith(window.location.origin)) return;
			// src = getProtectedImageUriFromStringResized('buyer')(src, 600, 600);
			try {
				const res = await fetch(src, { mode: 'cors' });
				const blob = await res.blob();
				const reader = new FileReader();

				const base64 = await new Promise<string>((resolve, reject) => {
					reader.onloadend = () => resolve(reader.result as string);
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});

				img.setAttribute('data-original-src', src); // optional: store original
				img.src = base64;
			} catch (err) {
				console.warn('Image failed to inline:', src, err);
			}
		})
	);
};
export const renderAttachmentsAsLinks = (container: HTMLElement) => {
	const anchors = container.querySelectorAll('a');

	anchors.forEach((a) => {
		const url = a.href;

		if (/\.(pdf|docx?|xlsx?|zip)$/i.test(url)) {
			// Add 📎 icon and clean label
			const filename = decodeURIComponent(url.split('/').pop() || 'attachment');
			a.innerText = `📎 ${filename}`;
		}
	});
};
